import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef,
  input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';

import { MatDatepicker } from '../../datepicker';

type Size = 'medium' | 'large';

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
    },
  ],
  standalone: false,
})
export class DateInputComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  public inputClasses = input<string>('');
  @Input() public placeHolder = '';
  public label = input('');
  public error = input<string>();
  public errorInFlow = input(false);
  @Input() public errorMessage = '';
  @Input() public warningMessage = '';
  @Input() public hintMessage = '';
  @Input() public tooltipHint = false;
  @Input() public isValid: boolean | undefined = undefined;
  @Input() public isWarning = false;
  @Input() public isAction = true;
  @Input() public onlyView: 'month' | 'year' | 'multi-year' | undefined = undefined;
  @Input() public selectEndOfPeriod = false;
  @Input() public minDate: DateTime | number | undefined | null = undefined;
  @Input() public maxDate: DateTime | number | undefined | null = undefined;
  @Input() public optional = false;
  @Input() public withTime = false;
  @Input() public outputType: 'date' | 'timestamp' = 'date';
  @Input() public allowFreeText = false;
  public isRequired = input(false);

  @Output() public changed: EventEmitter<DateTime | number | string | null> = new EventEmitter();

  @ViewChild(MatDatepicker) private readonly picker: MatDatepicker<DateTime> | null = null;

  public val: DateTime | null | string = null;
  public isDisabled = false;
  public isFocus = false;
  public isHover = false;

  private _size: Size = 'medium';
  private _calculatedSize: Size = this._size;

  private onChange: null | ((arg: DateTime | number | string | null) => unknown) = null;
  private onTouch: null | (() => unknown) = null;

  private readonly ngUnsubscribe$ = new Subject<void>();

  constructor(private readonly chRef: ChangeDetectorRef) {}

  public get minimumDate(): DateTime | undefined {
    return typeof this.minDate === 'number' ? DateTime.fromMillis(this.minDate) : (this.minDate ?? undefined);
  }

  public get maximumDate(): DateTime | undefined {
    return typeof this.maxDate === 'number' ? DateTime.fromMillis(this.maxDate) : (this.maxDate ?? undefined);
  }

  public get size(): Size {
    return this._calculatedSize;
  }

  @Input()
  public set size(size: Size) {
    this._size = size;
    this.calculateSize();
  }

  get value(): DateTime | null | string {
    return this.val;
  }

  set value(val: DateTime | null | string) {
    this.val = val;
    this.chRef.detectChanges();

    const formattedOutput = val && this.outputType === 'date' ? val : ((typeof val === 'string' ? -1 : val?.toMillis()) ?? null);
    this.onChange?.(formattedOutput);
    this.changed.emit(formattedOutput);
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  public ngAfterViewInit(): void {
    this.watchPickerOpenState();
  }

  public writeValue(value: DateTime | number | null): void {
    this.val = typeof value === 'number' ? DateTime.fromMillis(value) : value;
    this.chRef.detectChanges();
  }

  public registerOnChange(fn: () => unknown): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => unknown): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.chRef.detectChanges();
  }

  public onBlur(): void {
    this.onTouch?.();
  }

  public onMouseEnter(): void {
    this.isHover = true;
  }

  public onMouseLeave(): void {
    this.isHover = false;
  }

  private watchPickerOpenState(): void {
    this.picker?.openedStream.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.isFocus = true;
      this.chRef.detectChanges();
    });
    this.picker?.closedStream.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.isFocus = false;
      this.chRef.detectChanges();
      this.onBlur();
    });
  }

  private calculateSize(): void {
    this._calculatedSize = this._size;
    this.chRef.markForCheck();
  }
}
